/**
 * Wave 1u9f — Tab "Questionnaires" rendered inside the company DetailSheet.
 *
 * Lists, creates, edits, sends and downloads questionnaires attached to a
 * specific company. Templates come from /api/questionnaires-builder.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Plus, ClipboardList, Send, Download, Trash2, Pencil, Loader2, CheckCircle2, Mail, FileText, ExternalLink, Copy, Search, X, ChevronUp, ChevronDown, EyeOff, GitBranch } from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';
import { computeCompletion, isQuestionVisible } from '../lib/questionnaireLogic';

const KIND_LABEL = {
  needs_analysis: 'Analyse des besoins',
  positioning: 'Positionnement',
  satisfaction: 'Satisfaction',
  evaluation: 'Évaluation',
  other: 'Autre',
};

const STATUS_STYLE = {
  draft: { bg: '#F6F7FB', fg: '#6B6C85', label: 'Brouillon' },
  sent: { bg: '#FEF3C7', fg: '#92400E', label: 'Envoyé au client' },
  filled: { bg: '#E6FBF7', fg: '#0F766E', label: 'Complété' },
};

function fmtDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

export default function CompanyQuestionnairesTab({ companyId, companyName }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sending, setSending] = useState(null);

  const reload = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/questionnaires`);
      setList(data || []);
    } catch {
      setList([]);
    }
    setLoading(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [companyId]);

  const unseenCount = useMemo(() => list.filter((q) => q.status === 'filled' && q.fill_mode === 'external' && !q.seen_at).length, [list]);

  const markSeen = async (q) => {
    if (q.seen_at || q.fill_mode !== 'external' || q.status !== 'filled') return;
    try {
      await api.post(`/companies/${companyId}/questionnaires/${q.id}/mark-seen`);
      setList((arr) => arr.map((x) => (x.id === q.id ? { ...x, seen_at: new Date().toISOString() } : x)));
    } catch {
      // best-effort, ignore
    }
  };

  const onCreate = async (templateId) => {
    try {
      const { data } = await api.post(`/companies/${companyId}/questionnaires`, { template_id: templateId });
      toast({ title: 'Questionnaire créé', description: data.title });
      setCreateOpen(false);
      await reload();
      setEditing(data);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Création impossible' });
    }
  };

  const onDelete = async (q) => {
    if (!window.confirm(`Supprimer définitivement « ${q.title} » ?`)) return;
    try {
      await api.delete(`/companies/${companyId}/questionnaires/${q.id}`);
      toast({ title: 'Questionnaire supprimé' });
      await reload();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Suppression impossible' });
    }
  };

  const onDownload = async (q) => {
    try {
      const res = await api.get(`/companies/${companyId}/questionnaires/${q.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `questionnaire-${(companyName || 'entreprise').replace(/\s/g, '_')}-${q.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Erreur PDF', description: err?.response?.data?.detail || 'Téléchargement impossible' });
    }
  };

  return (
    <div data-testid="company-questionnaires-tab" className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-[13.5px] font-semibold text-[#1A1B2E] flex items-center gap-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Questionnaires ({list.length})
          {unseenCount > 0 && (
            <span data-testid="company-q-unseen-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#4F46E5] text-[10.5px] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-[#4F46E5]" />
              {unseenCount} nouvelle{unseenCount > 1 ? 's' : ''} soumission{unseenCount > 1 ? 's' : ''}
            </span>
          )}
        </h4>
        <button
          data-testid="company-q-new-btn"
          type="button"
          onClick={async () => {
            try {
              const { data } = await api.get('/questionnaires-builder');
              setTemplates(data || []);
              setCreateOpen(true);
            } catch {
              toast({ title: 'Erreur', description: 'Impossible de charger les modèles' });
            }
          }}
          className="h-8 px-3 rounded-lg brand-gradient text-white text-[12px] font-semibold inline-flex items-center gap-1.5 shadow-sm"
        >
          <Plus className="w-3.5 h-3.5" /> Nouveau questionnaire
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#4F46E5]" /></div>
      ) : list.length === 0 ? (
        <div className="rounded-xl bg-[#FAFAFE] border border-dashed border-[#EFF0F6] p-6 text-center">
          <ClipboardList className="w-7 h-7 text-[#8E8FA5] mx-auto mb-2" />
          <p className="text-[12.5px] text-[#6B6C85]">Aucun questionnaire pour cette entreprise.</p>
          <p className="text-[11.5px] text-[#8E8FA5] mt-1">Cliquez sur « Nouveau questionnaire » pour démarrer à partir d'un modèle.</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
          {list.map((q) => {
            const s = STATUS_STYLE[q.status] || STATUS_STYLE.draft;
            const isUnseen = q.status === 'filled' && q.fill_mode === 'external' && !q.seen_at;
            return (
              <div key={q.id} data-testid={`company-q-${q.id}`} className="relative flex items-center gap-3 p-3 rounded-xl border bg-[#FAFAFE] border-[#EFF0F6] hover:border-[#C7D2FE] transition-colors">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#EEF2FF', color: '#4F46E5' }}>
                  <FileText className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{q.title}</div>
                    <span className="text-[10.5px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#4F46E5]">{KIND_LABEL[q.kind] || q.kind}</span>
                    <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: s.bg, color: s.fg }}>{s.label}</span>
                  </div>
                  <div className="text-[11.5px] text-[#8E8FA5] mt-0.5">
                    {q.filled_at && <>Complété {fmtDate(q.filled_at)} {q.fill_mode === 'external' ? '(client)' : `(${q.filled_by_name || 'OF'})`}</>}
                    {/* Ce que son retour a change. « Validé » et « corrigé trois
                        réponses » ne prouvent pas la même chose sur une analyse
                        du besoin, et c'est ce qu'un auditeur regarde. */}
                    {q.releve?.valide_sans_changement && (
                      <span className="ml-1 font-semibold text-[#0F766E]">
                        · validé sans changement
                      </span>
                    )}
                    {q.releve?.modifies?.length > 0 && (
                      <span className="ml-1 font-semibold text-[#B45309]">
                        · {q.releve.modifies.length} réponse(s) corrigée(s)
                      </span>
                    )}
                    {q.prerempli_par && !q.filled_at && (
                      <span className="ml-1">· pré-rempli par {q.prerempli_par}</span>
                    )}
                    {!q.filled_at && q.sent_at && <>Envoyé à {q.sent_to} le {fmtDate(q.sent_at)} — en attente de réponse</>}
                    {!q.filled_at && !q.sent_at && <>Créé le {fmtDate(q.created_at)}</>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`company-q-edit-${q.id}`} type="button" onClick={() => { markSeen(q); setEditing(q); }} title="Modifier / Remplir" className="w-8 h-8 rounded-lg hover:bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center"><Pencil className="w-3.5 h-3.5" /></button>
                  <button data-testid={`company-q-send-${q.id}`} type="button" onClick={() => setSending(q)} title="Envoyer au client" className="w-8 h-8 rounded-lg hover:bg-[#FEF3C7] text-[#92400E] flex items-center justify-center"><Send className="w-3.5 h-3.5" /></button>
                  <button data-testid={`company-q-pdf-${q.id}`} type="button" onClick={() => { markSeen(q); onDownload(q); }} title="Télécharger PDF" className="w-8 h-8 rounded-lg hover:bg-[#F6F7FB] text-[#6B6C85] flex items-center justify-center"><Download className="w-3.5 h-3.5" /></button>
                  <button data-testid={`company-q-del-${q.id}`} type="button" onClick={() => onDelete(q)} title="Supprimer" className="w-8 h-8 rounded-lg hover:bg-[#FEF2F2] text-[#EF4444] flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                {/* Wave 1u9l — keep only the discreet count in the header (no per-row badge anymore) */}
                {isUnseen && <span className="sr-only">Nouvelle soumission non vue</span>}
              </div>
            );
          })}
        </div>
      )}

      {createOpen && (
        <PickTemplateDialog templates={templates} onPick={onCreate} onClose={() => setCreateOpen(false)} />
      )}

      {editing && (
        <FillDialog
          companyId={companyId}
          questionnaire={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { await reload(); setEditing(null); }}
        />
      )}

      {sending && (
        <SendDialog
          companyId={companyId}
          questionnaire={sending}
          onClose={() => setSending(null)}
          onSent={async () => { await reload(); setSending(null); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-dialog: pick a template (with search + kind filter)
// ---------------------------------------------------------------------------
function PickTemplateDialog({ templates, onPick, onClose }) {
  const [pick, setPick] = useState('');
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('all');

  const kinds = useMemo(() => {
    const set = new Set(templates.map((t) => t.kind));
    return ['all', ...Array.from(set)];
  }, [templates]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) => {
      if (kindFilter !== 'all' && t.kind !== kindFilter) return false;
      if (!q) return true;
      return (t.title || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q);
    });
  }, [templates, search, kindFilter]);

  // Default pick when filters change
  useEffect(() => {
    if (filtered.length === 0) { setPick(''); return; }
    if (!filtered.some((t) => t.id === pick)) setPick(filtered[0].id);
  }, [filtered, pick]);

  return (
    <Modal title="Choisir un modèle de questionnaire" onClose={onClose} testid="company-q-pick">
      {templates.length === 0 ? (
        <p className="text-[13px] text-[#6B6C85]">Aucun modèle disponible. Créez-en d'abord dans <strong>Paramètres → Questionnaires</strong>.</p>
      ) : (
        <>
          {/* Search + kind filter */}
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#8E8FA5]" />
              <input
                data-testid="company-q-pick-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un modèle…"
                className="w-full h-10 pl-9 pr-3 rounded-xl border border-[#EFF0F6] bg-white text-[13px] focus:border-[#4F46E5] focus:outline-none"
              />
            </div>
            <select
              data-testid="company-q-pick-kind-filter"
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value)}
              className="h-10 px-3 rounded-xl border border-[#EFF0F6] bg-white text-[13px] focus:border-[#4F46E5] focus:outline-none"
            >
              {kinds.map((k) => (
                <option key={k} value={k}>{k === 'all' ? 'Tous les types' : (KIND_LABEL[k] || k)}</option>
              ))}
            </select>
          </div>

          {/* Filtered list */}
          <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
            {filtered.length === 0 ? (
              <div className="text-[12.5px] text-[#8E8FA5] italic px-2 py-3">Aucun modèle ne correspond à votre recherche.</div>
            ) : filtered.map((t) => {
              const sel = pick === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`company-q-pick-${t.id}`}
                  type="button"
                  onClick={() => setPick(t.id)}
                  className={`w-full text-left p-3 rounded-xl border transition-colors ${sel ? 'border-[#4F46E5] bg-[#EEF2FF]' : 'border-[#EFF0F6] bg-white hover:border-[#C7D2FE]'}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="text-[13px] font-semibold text-[#1A1B2E] flex-1 truncate">{t.title}</div>
                    <span className="text-[10.5px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#4F46E5] shrink-0">{KIND_LABEL[t.kind] || t.kind}</span>
                  </div>
                  {t.description && <div className="text-[11.5px] text-[#8E8FA5] mt-0.5 truncate">{t.description}</div>}
                  <div className="text-[10.5px] text-[#8E8FA5] mt-1">{(t.questions || []).length} question(s)</div>
                </button>
              );
            })}
          </div>

          <p className="text-[11.5px] text-[#8E8FA5] mt-3">Le modèle sera copié à l'instant T. Vos éditions ultérieures n'affecteront pas le modèle d'origine.</p>
        </>
      )}
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="h-10 px-4 rounded-lg text-[13px] text-[#6B6C85] hover:bg-[#F6F7FB]">Annuler</button>
        {templates.length > 0 && (
          <button data-testid="company-q-pick-confirm" disabled={!pick} onClick={() => onPick(pick)} className="h-10 px-4 rounded-lg brand-gradient text-white text-[13px] font-semibold disabled:opacity-50">Créer</button>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Sub-dialog: fill / edit the questionnaire (OF side)
// ---------------------------------------------------------------------------
function FillDialog({ companyId, questionnaire, onClose, onSaved }) {
  const [title, setTitle] = useState(questionnaire.title);
  const [questions, setQuestions] = useState(questionnaire.questions || []);
  const [answers, setAnswers] = useState(questionnaire.answers || {});
  const [busy, setBusy] = useState(false);
  const [editStructure, setEditStructure] = useState(false);

  // Wave 1u9j — live completion + visibility honour show_if + goto_per_option
  const completion = useMemo(() => computeCompletion(questions || [], answers || {}), [questions, answers]);

  const setA = (qid, v) => setAnswers((p) => ({ ...p, [qid]: v }));
  const updateQ = (i, patch) => setQuestions((arr) => arr.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const addQuestion = () => {
    const id = `q${Date.now().toString(36)}`;
    setQuestions((arr) => [...arr, { id, type: 'text', label: '', required: false, options: [], scale_min: 1, scale_max: 5 }]);
  };
  const removeQ = (i) => {
    const q = questions[i];
    setQuestions((arr) => arr.filter((_, idx) => idx !== i));
    if (q?.id) setAnswers((p) => { const next = { ...p }; delete next[q.id]; return next; });
  };
  const moveQ = (i, dir) => {
    setQuestions((arr) => {
      const next = [...arr];
      const j = i + dir;
      if (j < 0 || j >= next.length) return next;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const save = async (download) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/companies/${companyId}/questionnaires/${questionnaire.id}`, { title, questions, answers });
      const isFilled = data.status === 'filled';
      const missing = (questions || []).filter((q) => {
        const v = answers[q.id];
        const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
        return q.required && empty;
      });
      if (isFilled) {
        toast({ title: 'Questionnaire complet', description: 'Toutes les réponses sont enregistrées.' });
      } else if (missing.length > 0) {
        toast({
          title: 'Enregistré en brouillon',
          description: `${missing.length} réponse(s) obligatoire(s) manquante(s). Le statut « Rempli » s'applique uniquement lorsque toutes les réponses requises sont fournies.`,
        });
      } else {
        toast({ title: 'Enregistré en brouillon', description: 'Le questionnaire reste modifiable.' });
      }
      if (download) {
        const res = await api.get(`/companies/${companyId}/questionnaires/${data.id}/pdf`, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `questionnaire-${data.id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
      await onSaved();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Enregistrement impossible' });
    }
    setBusy(false);
  };

  return (
    <Modal
      title="Remplir / modifier le questionnaire"
      onClose={onClose}
      testid="company-q-fill"
      wide
      footer={(
        <>
          <button onClick={onClose} className="h-10 px-4 rounded-lg text-[13px] text-[#6B6C85] hover:bg-white">Fermer</button>
          <button data-testid="company-q-fill-save" disabled={busy} onClick={() => save(false)} className="h-10 px-4 rounded-lg bg-[#F6F7FB] hover:bg-[#EFF0F6] text-[#1A1B2E] text-[13px] font-semibold">Enregistrer</button>
          <button data-testid="company-q-fill-save-pdf" disabled={busy} onClick={() => save(true)} className="h-10 px-4 rounded-lg brand-gradient text-white text-[13px] font-semibold inline-flex items-center gap-1.5">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Enregistrer + Télécharger PDF
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-[12px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">Titre</label>
            <input data-testid="company-q-fill-title" value={title} onChange={(e) => setTitle(e.target.value)}
              className="w-full h-11 px-3 rounded-xl border border-[#EFF0F6] bg-white text-[13.5px] focus:border-[#4F46E5] focus:outline-none" />
          </div>
          <button
            type="button"
            data-testid="company-q-fill-toggle-struct"
            onClick={() => setEditStructure((v) => !v)}
            className={`h-11 px-4 rounded-xl text-[12.5px] font-semibold inline-flex items-center gap-1.5 ${editStructure ? 'bg-[#4F46E5] text-white' : 'bg-[#F6F7FB] text-[#6B6C85] hover:bg-[#EFF0F6]'}`}
            title="Activer l'édition de la structure des questions"
          >
            <Pencil className="w-3.5 h-3.5" /> {editStructure ? 'Mode édition' : 'Modifier la structure'}
          </button>
        </div>

        {/* Live completion indicator */}
        <div data-testid="company-q-fill-completion" className={`rounded-xl border p-3 ${completion.allRequiredOk ? 'bg-[#E6FBF7] border-[#99F6E4]' : 'bg-[#FAFAFE] border-[#EFF0F6]'}`}>
          <div className="flex items-center justify-between gap-3 mb-1.5">
            <div className="flex items-center gap-2 text-[12px] font-semibold text-[#1A1B2E]">
              {completion.allRequiredOk
                ? <><CheckCircle2 className="w-3.5 h-3.5 text-[#0F766E]" /> Toutes les réponses obligatoires sont fournies — prêt à passer en « Rempli »</>
                : <><Loader2 className="w-3.5 h-3.5 text-[#4F46E5]" /> Complétez les questions obligatoires pour valider le questionnaire</>}
            </div>
            <span className="text-[11.5px] font-bold tabular-nums" style={{ color: completion.allRequiredOk ? '#0F766E' : '#4F46E5' }}>
              {completion.pct}%
            </span>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-[#6B6C85]">
            <span>
              <strong className="text-[#1A1B2E] tabular-nums">{completion.reqDone}</strong> / {completion.totalRequired || '0'} obligatoires
            </span>
            {completion.optDone > 0 && (
              <span>
                <strong className="text-[#1A1B2E] tabular-nums">{completion.optDone}</strong> facultatives renseignées
              </span>
            )}
            <span className="text-[#8E8FA5]">·</span>
            <span>{completion.totalQuestions} question{completion.totalQuestions > 1 ? 's' : ''} au total</span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-white overflow-hidden border border-[#EFF0F6]">
            <div
              data-testid="company-q-fill-progress-bar"
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${completion.pct}%`,
                background: completion.allRequiredOk
                  ? 'linear-gradient(90deg, #14B8A6, #0F766E)'
                  : 'linear-gradient(90deg, #A5B4FC, #4F46E5)',
              }}
            />
          </div>
        </div>

        <div className="space-y-3 pt-2">
          {questions.map((q, i) => {
            const isEmpty = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
            const answered = !isEmpty(answers[q.id]);
            // Wave 1u9j — hide questions whose show_if/goto exclude them, unless
            // we are editing the structure (in which case the OF needs to see
            // and tweak every question regardless).
            const visible = editStructure || isQuestionVisible(q, questions, answers);
            if (!visible) return null;
            return (
            <div key={q.id} data-testid={`company-q-field-${q.id}`} className={`rounded-xl border p-3.5 transition-colors ${q.required && !answered ? 'bg-[#FFFBEB] border-[#FCD34D]' : answered ? 'bg-[#F0FDF4] border-[#BBF7D0]' : 'bg-[#FAFAFE] border-[#EFF0F6]'}`}>
              <div className="flex items-start gap-2 mb-2">
                <span className="w-6 h-6 rounded-md bg-[#EEF2FF] text-[#4F46E5] text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <input
                  data-testid={`company-q-field-label-${q.id}`}
                  value={q.label || ''}
                  onChange={(e) => updateQ(i, { label: e.target.value })}
                  className="flex-1 text-[13.5px] font-semibold text-[#1A1B2E] bg-transparent border-0 focus:outline-none focus:bg-white focus:px-2 focus:rounded transition-all"
                  placeholder={`Question ${i + 1}`}
                />
                {q.required && !answered && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E] shrink-0">À remplir</span>
                )}
                {answered && (
                  <CheckCircle2 className="w-4 h-4 text-[#0F766E] shrink-0 mt-1" />
                )}
                {editStructure && (
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button type="button" onClick={() => moveQ(i, -1)} disabled={i === 0} title="Monter" className="w-7 h-7 rounded-lg hover:bg-[#EEF2FF] text-[#6B6C85] flex items-center justify-center disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                    <button type="button" onClick={() => moveQ(i, 1)} disabled={i === questions.length - 1} title="Descendre" className="w-7 h-7 rounded-lg hover:bg-[#EEF2FF] text-[#6B6C85] flex items-center justify-center disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                    <button data-testid={`company-q-field-del-${q.id}`} type="button" onClick={() => removeQ(i)} title="Supprimer" className="w-7 h-7 rounded-lg hover:bg-[#FEF2F2] text-[#EF4444] flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>

              {editStructure && (
                <QuestionStructureEditor question={q} onChange={(patch) => updateQ(i, patch)} idx={i} allQuestions={questions} />
              )}

              <AnswerInput question={q} value={answers[q.id]} onChange={(v) => setA(q.id, v)} />
            </div>
            );
          })}

          {editStructure && (
            <button
              data-testid="company-q-add-question"
              type="button"
              onClick={addQuestion}
              className="w-full h-10 rounded-xl border-2 border-dashed border-[#C7D2FE] text-[#4F46E5] text-[12.5px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#EEF2FF]"
            >
              <Plus className="w-3.5 h-3.5" /> Ajouter une question
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

// Inline editor for question structure (type + options + scale + show_if + goto_per_option).
function QuestionStructureEditor({ question, onChange, idx, allQuestions }) {
  const updateOpt = (i, v) => onChange({ options: (question.options || []).map((o, oi) => (oi === i ? v : o)) });
  const removeOpt = (i) => onChange({ options: (question.options || []).filter((_, oi) => oi !== i) });
  const addOpt = () => onChange({ options: [...(question.options || []), ''] });

  // Wave 1u9j — show_if (conditional display) multi-criteria
  const eligibleRefs = (allQuestions || []).slice(0, idx).filter((q) => ['single', 'multi', 'scale', 'text'].includes(q.type));
  const conditions = useMemo(() => {
    if (!question.show_if) return null;
    if (Array.isArray(question.show_if.conditions)) return question.show_if;
    if (question.show_if.question_id) {
      return { mode: 'AND', conditions: [{ question_id: question.show_if.question_id, op: 'equals', value: question.show_if.equals ?? '' }] };
    }
    return null;
  }, [question.show_if]);
  const showIfEnabled = !!conditions;
  const updateConditions = (next) => onChange({ show_if: next ? { mode: next.mode || 'AND', conditions: next.conditions || [] } : null });
  const addCondition = () => {
    const ref = eligibleRefs[0];
    if (!ref) return;
    const cur = conditions || { mode: 'AND', conditions: [] };
    updateConditions({ ...cur, conditions: [...cur.conditions, { question_id: ref.id, op: 'equals', value: '' }] });
  };
  const removeCondition = (i) => {
    if (!conditions) return;
    const next = conditions.conditions.filter((_, ci) => ci !== i);
    updateConditions(next.length ? { ...conditions, conditions: next } : null);
  };
  const setCondition = (i, patch) => {
    if (!conditions) return;
    const next = conditions.conditions.map((c, ci) => (ci === i ? { ...c, ...patch } : c));
    updateConditions({ ...conditions, conditions: next });
  };

  // Wave 1u9j — goto_per_option (skip logic) — single-choice only
  const laterRefs = (allQuestions || []).slice(idx + 1);
  const gotoEnabled = !!question.goto_per_option;
  const setGoto = (opt, target) => {
    const next = { ...(question.goto_per_option || {}) };
    if (target) next[opt] = target; else delete next[opt];
    onChange({ goto_per_option: Object.keys(next).length ? next : null });
  };

  return (
    <div className="rounded-lg bg-white border border-[#EFF0F6] p-2.5 mb-2 space-y-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8FA5]">Type</label>
        <select
          data-testid={`company-q-struct-type-${question.id}`}
          value={question.type}
          onChange={(e) => onChange({ type: e.target.value })}
          className="h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12px]"
        >
          <option value="text">Texte court</option>
          <option value="long_text">Texte long</option>
          <option value="single">Choix unique</option>
          <option value="multi">Choix multiples</option>
          <option value="scale">Échelle</option>
        </select>
        <label className="inline-flex items-center gap-1.5 text-[12px] text-[#4A4B63] ml-2">
          <input type="checkbox" checked={!!question.required} onChange={(e) => onChange({ required: e.target.checked })} />
          Obligatoire
        </label>
      </div>

      {(question.type === 'single' || question.type === 'multi') && (
        <div className="space-y-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8FA5]">Options</div>
          {(question.options || []).map((opt, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <input
                data-testid={`company-q-struct-opt-${question.id}-${i}`}
                value={opt}
                onChange={(e) => updateOpt(i, e.target.value)}
                placeholder={`Option ${i + 1}`}
                className="flex-1 h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px] focus:border-[#4F46E5] focus:outline-none"
              />
              <button type="button" onClick={() => removeOpt(i)} className="w-7 h-7 rounded-lg hover:bg-[#FEF2F2] text-[#EF4444] flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <button
            data-testid={`company-q-struct-add-opt-${question.id}`}
            type="button"
            onClick={addOpt}
            className="h-7 px-2.5 rounded-lg bg-[#EEF2FF] text-[#4F46E5] text-[11.5px] font-semibold inline-flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Ajouter une option
          </button>
        </div>
      )}

      {question.type === 'scale' && (
        <div className="flex items-center gap-3 text-[12px] text-[#4A4B63]">
          <label className="inline-flex items-center gap-1.5">Min
            <input type="number" min={0} max={9} value={question.scale_min ?? 1} onChange={(e) => onChange({ scale_min: parseInt(e.target.value || '1', 10) })} className="w-14 h-7 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12px]" />
          </label>
          <label className="inline-flex items-center gap-1.5">Max
            <input type="number" min={1} max={20} value={question.scale_max ?? 5} onChange={(e) => onChange({ scale_max: parseInt(e.target.value || '5', 10) })} className="w-14 h-7 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12px]" />
          </label>
        </div>
      )}

      {/* Conditional display (show_if) */}
      {idx > 0 && (
        <div className="rounded-lg border border-dashed border-[#EFF0F6] bg-[#FAFAFE] p-2.5">
          <label className="flex items-center gap-1.5 text-[11.5px] font-semibold text-[#4A4B63] cursor-pointer">
            <input
              type="checkbox"
              data-testid={`company-q-show-if-toggle-${question.id}`}
              checked={showIfEnabled}
              onChange={(e) => {
                if (!e.target.checked) updateConditions(null);
                else {
                  const ref = eligibleRefs[0];
                  if (ref) updateConditions({ mode: 'AND', conditions: [{ question_id: ref.id, op: 'equals', value: '' }] });
                }
              }}
              className="accent-[#4F46E5]"
              disabled={eligibleRefs.length === 0}
            />
            <EyeOff className="w-3 h-3 text-[#6B6C85]" /> Afficher seulement si…
            {eligibleRefs.length === 0 && <span className="text-[10.5px] text-[#8E8FA5] italic">(aucune question précédente compatible)</span>}
          </label>
          {showIfEnabled && (
            <div className="mt-2 space-y-1.5">
              {conditions.conditions.length > 1 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[10.5px] text-[#6B6C85]">Combiner avec :</span>
                  <div className="flex bg-[#F6F7FB] rounded-md p-0.5">
                    {['AND', 'OR'].map((m) => (
                      <button
                        key={m}
                        type="button"
                        data-testid={`company-q-show-if-mode-${m.toLowerCase()}-${question.id}`}
                        onClick={() => updateConditions({ ...conditions, mode: m })}
                        className={`px-2.5 h-6 text-[10.5px] font-bold rounded ${conditions.mode === m ? 'bg-white shadow-sm text-[#1A1B2E]' : 'text-[#6B6C85]'}`}
                      >
                        {m === 'AND' ? 'TOUTES (ET)' : 'AU MOINS UNE (OU)'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {conditions.conditions.map((cond, ci) => {
                const refQ = (allQuestions || []).find((q) => q.id === cond.question_id);
                const refOpts = (refQ?.type === 'single' || refQ?.type === 'multi')
                  ? (refQ.options || [])
                  : refQ?.type === 'scale'
                    ? Array.from({ length: (refQ.scale_max ?? 5) - (refQ.scale_min ?? 1) + 1 }, (_, i) => String((refQ.scale_min ?? 1) + i))
                    : [];
                return (
                  <div key={ci} className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-1.5">
                    <select
                      data-testid={`company-q-cond-${question.id}-${ci}-ref`}
                      value={cond.question_id}
                      onChange={(e) => setCondition(ci, { question_id: e.target.value, value: '' })}
                      className="h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[11.5px]"
                    >
                      {eligibleRefs.map((qq, i) => <option key={qq.id} value={qq.id}>{i + 1}. {(qq.label || '(sans libellé)').slice(0, 40)}</option>)}
                    </select>
                    <select
                      data-testid={`company-q-cond-${question.id}-${ci}-op`}
                      value={cond.op || 'equals'}
                      onChange={(e) => setCondition(ci, { op: e.target.value })}
                      className="h-8 px-1.5 rounded-lg border border-[#EFF0F6] bg-white text-[11px]"
                    >
                      <option value="equals">est</option>
                      <option value="not_equals">n'est pas</option>
                      <option value="gte">≥</option>
                      <option value="lte">≤</option>
                      <option value="in">contient</option>
                    </select>
                    {refOpts.length > 0 ? (
                      <select
                        data-testid={`company-q-cond-${question.id}-${ci}-val`}
                        value={String(cond.value ?? '')}
                        onChange={(e) => setCondition(ci, { value: refQ?.type === 'scale' ? parseInt(e.target.value, 10) : e.target.value })}
                        className="h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[11.5px]"
                      >
                        <option value="">— choisir —</option>
                        {refOpts.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    ) : (
                      <input
                        type="text"
                        data-testid={`company-q-cond-${question.id}-${ci}-val`}
                        value={cond.value ?? ''}
                        onChange={(e) => setCondition(ci, { value: e.target.value })}
                        placeholder="Valeur"
                        className="h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[11.5px]"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => removeCondition(ci)}
                      className="w-7 h-7 rounded-lg hover:bg-[#FEE2E2] text-[#EF4444] flex items-center justify-center"
                      title="Retirer cette condition"
                    ><X className="w-3 h-3" /></button>
                  </div>
                );
              })}
              <button
                type="button"
                data-testid={`company-q-add-cond-${question.id}`}
                onClick={addCondition}
                className="text-[11px] text-[#4F46E5] font-semibold hover:underline"
              >+ Ajouter une condition</button>
            </div>
          )}
        </div>
      )}

      {/* Skip logic (goto_per_option) — single only */}
      {question.type === 'single' && (question.options || []).length > 0 && laterRefs.length > 0 && (
        <div className="rounded-lg border border-dashed border-[#EFF0F6] bg-[#FAFAFE] p-2.5">
          <label className="flex items-center gap-1.5 text-[11.5px] font-semibold text-[#4A4B63] cursor-pointer">
            <input
              type="checkbox"
              data-testid={`company-q-goto-toggle-${question.id}`}
              checked={gotoEnabled}
              onChange={(e) => onChange({ goto_per_option: e.target.checked ? {} : null })}
              className="accent-[#4F46E5]"
            />
            <GitBranch className="w-3 h-3 text-[#6B6C85]" /> Saut conditionnel selon la réponse
          </label>
          {gotoEnabled && (
            <div className="mt-2 space-y-1.5">
              <div className="text-[10.5px] text-[#8E8FA5] italic">Pour chaque option, choisissez la prochaine question à afficher.</div>
              {(question.options || []).map((opt) => (
                <div key={opt} className="grid grid-cols-[1fr_auto_1fr] items-center gap-1.5">
                  <span className="text-[12px] text-[#1A1B2E] truncate">{opt || <em className="text-[#8E8FA5]">(option vide)</em>}</span>
                  <span className="text-[11px] text-[#6B6C85]">→</span>
                  <select
                    data-testid={`company-q-goto-${question.id}-${opt.replace(/\s+/g, '_')}`}
                    value={(question.goto_per_option || {})[opt] || ''}
                    onChange={(e) => setGoto(opt, e.target.value)}
                    className="h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[11.5px]"
                  >
                    <option value="">Continuer normalement</option>
                    {laterRefs.map((lr, i) => (
                      <option key={lr.id} value={lr.id}>Aller à Q{idx + 2 + i}. {(lr.label || '').slice(0, 32)}</option>
                    ))}
                    <option value="__end__">Terminer le questionnaire</option>
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AnswerInput({ question, value, onChange }) {
  if (question.type === 'long_text') {
    return <textarea rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-[#EFF0F6] bg-white text-[13px] resize-none focus:border-[#4F46E5] focus:outline-none" />;
  }
  if (question.type === 'single') {
    return (
      <div className="flex flex-wrap gap-1.5">
        {(question.options || []).map((opt) => {
          const sel = value === opt;
          return (
            <button key={opt} type="button" onClick={() => onChange(opt)} className={`h-8 px-3 rounded-lg text-[12.5px] border ${sel ? 'bg-[#4F46E5] text-white border-[#4F46E5]' : 'bg-white border-[#EFF0F6] hover:border-[#C7D2FE]'}`}>{opt}</button>
          );
        })}
      </div>
    );
  }
  if (question.type === 'multi') {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => onChange(arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt]);
    return (
      <div className="flex flex-wrap gap-1.5">
        {(question.options || []).map((opt) => {
          const sel = arr.includes(opt);
          return (
            <button key={opt} type="button" onClick={() => toggle(opt)} className={`h-8 px-3 rounded-lg text-[12.5px] border ${sel ? 'bg-[#4F46E5] text-white border-[#4F46E5]' : 'bg-white border-[#EFF0F6] hover:border-[#C7D2FE]'}`}>{opt}</button>
          );
        })}
      </div>
    );
  }
  if (question.type === 'scale') {
    const min = question.scale_min ?? 1;
    const max = question.scale_max ?? 5;
    const items = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    return (
      <div className="flex flex-wrap gap-1.5">
        {items.map((n) => {
          const sel = value === n;
          return (
            <button key={n} type="button" onClick={() => onChange(n)} className={`w-9 h-9 rounded-lg text-[13px] font-semibold border ${sel ? 'bg-[#4F46E5] text-white border-[#4F46E5]' : 'bg-white border-[#EFF0F6] hover:border-[#C7D2FE]'}`}>{n}</button>
          );
        })}
      </div>
    );
  }
  // text fallback
  return <input value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[13px] focus:border-[#4F46E5] focus:outline-none" />;
}

// ---------------------------------------------------------------------------
// Sub-dialog: send to client (with copy public link)
// ---------------------------------------------------------------------------
function SendDialog({ companyId, questionnaire, onClose, onSent }) {
  const [to, setTo] = useState(questionnaire.sent_to || '');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    if (!to || !to.includes('@')) {
      toast({ title: 'Adresse e-mail invalide' });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/questionnaires/${questionnaire.id}/send-to-client`, { to, message });
      setResult(data);
      // « Aucun SMTP configuré » était une cause inventée : la boîte l'était,
      // c'est le dernier recours qui ne valait rien. On dit la vraie raison.
      toast({
        title: data.email_sent ? 'E-mail envoyé' : "Lien généré, e-mail non parti",
        description: data.email_sent
          ? `À ${data.to}`
          : `${data.email_error || 'Raison inconnue'} — copiez le lien ci-dessous.`,
      });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Envoi impossible' });
    }
    setBusy(false);
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Lien copié' });
    } catch {
      toast({ title: 'Impossible de copier' });
    }
  };

  return (
    <Modal title="Envoyer le questionnaire au client" onClose={onClose} testid="company-q-send">
      {!result ? (
        <div className="space-y-3">
          <div>
            <label className="block text-[12px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">E-mail destinataire</label>
            <input data-testid="company-q-send-to" type="email" value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="contact@entreprise.fr"
              className="w-full h-11 px-3 rounded-xl border border-[#EFF0F6] bg-white text-[13.5px] focus:border-[#4F46E5] focus:outline-none" />
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">Message (optionnel)</label>
            <textarea data-testid="company-q-send-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Mot personnalisé pour accompagner le lien."
              className="w-full px-3 py-2 rounded-xl border border-[#EFF0F6] bg-white text-[13px] resize-none focus:border-[#4F46E5] focus:outline-none" />
          </div>
          <p className="text-[11.5px] text-[#8E8FA5]">Le client recevra un lien sécurisé unique. Vous serez notifié à sa soumission.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="rounded-xl bg-[#E6FBF7] border border-[#99F6E4] p-3.5 flex items-start gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-[#0F766E] mt-0.5 shrink-0" />
            <div className="text-[12.5px] text-[#0F766E]">
              {result.email_sent ? <>E-mail envoyé à <strong>{result.to}</strong>.</> : <>Lien généré (e-mail non envoyé — vérifiez votre config SMTP).</>}
            </div>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">Lien public</label>
            <div className="flex items-center gap-1.5">
              <input data-testid="company-q-send-link" readOnly value={result.public_url}
                className="flex-1 h-10 px-3 rounded-lg border border-[#EFF0F6] bg-[#FAFAFE] text-[12px] text-[#1A1B2E] truncate" />
              <button data-testid="company-q-send-copy" onClick={() => copy(result.public_url)} className="h-10 px-3 rounded-lg bg-[#EEF2FF] text-[#4F46E5] text-[12px] font-semibold inline-flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Copier</button>
              <a href={result.public_url} target="_blank" rel="noreferrer" className="h-10 px-3 rounded-lg bg-[#F6F7FB] text-[#6B6C85] text-[12px] font-semibold inline-flex items-center gap-1.5"><ExternalLink className="w-3.5 h-3.5" /> Ouvrir</a>
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 mt-5">
        {!result ? (
          <>
            <button onClick={onClose} className="h-10 px-4 rounded-lg text-[13px] text-[#6B6C85] hover:bg-[#F6F7FB]">Annuler</button>
            <button data-testid="company-q-send-go" onClick={send} disabled={busy} className="h-10 px-4 rounded-lg brand-gradient text-white text-[13px] font-semibold inline-flex items-center gap-1.5">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Envoyer
            </button>
          </>
        ) : (
          <button onClick={onSent} className="h-10 px-4 rounded-lg brand-gradient text-white text-[13px] font-semibold">Fermer</button>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Re-usable modal shell (no shadcn dependency to keep this file self-contained)
// ---------------------------------------------------------------------------
function Modal({ title, children, footer, onClose, testid, wide }) {
  return (
    <div data-testid={testid} className="fixed inset-0 z-[120] bg-[#1A1B2E]/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] ${wide ? 'max-w-3xl' : 'max-w-lg'} flex flex-col overflow-hidden`}>
        <div className="px-5 py-3.5 border-b border-[#EFF0F6] flex items-center justify-between shrink-0">
          <h3 className="text-[15px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-[#F6F7FB] text-[#6B6C85] text-[18px] leading-none">×</button>
        </div>
        <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">{children}</div>
        {footer && (
          <div className="px-5 py-3 border-t border-[#EFF0F6] bg-[#FAFAFE] shrink-0 flex items-center justify-end gap-2">{footer}</div>
        )}
      </div>
    </div>
  );
}
